import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import {
  listGarageOrdersFiltered,
  getGarageOrderCounts,
  OrderListItem,
  OrderCounts,
  PartnerOrderFilter,
} from '../services/api';
import { GarageDetailResponse } from '../types';
import { hasGaragatLeg, methodLabel, selfLegHints } from '../utils/orderLegs';
import { selectedOptions } from '../utils/serviceLines';

const PAGE_SIZE = 20;

const TABS: { label: string; value: PartnerOrderFilter }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'New orders', value: 'NEW' },
  { label: 'In progress', value: 'IN_PROGRESS' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Closed', value: 'CLOSED' },
];

// Human-readable labels for the external (partner-visible) order statuses.
const STATUS_LABELS: Record<string, string> = {
  CREATED: 'New',
  CONFIRMED: 'Confirmed',
  PICKUP_ASSIGNED: 'Driver assigned · pickup',
  IN_PROGRESS: 'In service',
  SERVICE_COMPLETE: 'Service complete',
  READY_FOR_PICKUP: 'Ready for return',
  DROPOFF_ASSIGNED: 'Driver assigned · return',
  COMPLETED: 'Completed',
  SETTLED: 'Settled',
  DISPUTED: 'Disputed',
  CANCELLED: 'Cancelled',
  ADMIN_CLOSED: 'Closed',
};

function statusLabel(status: string): string {
  return (
    STATUS_LABELS[status] ??
    status
      .split('_')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ')
  );
}

function statusColor(status: string): { bg: string; fg: string } {
  switch (status) {
    case 'CREATED':
    case 'PENDING':
      return { bg: '#fef3c7', fg: '#854d0e' };
    case 'PICKUP_ASSIGNED':
    case 'PICKUP_SCHEDULED':
    case 'CAR_PICKED_UP':
    case 'CAR_DELIVERED_TO_GARAGE':
    case 'DROPOFF_ASSIGNED':
    case 'CONFIRMED':
    case 'ACTIVE':
      return { bg: '#dbeafe', fg: '#1e40af' };
    case 'IN_SERVICE':
    case 'IN_PROGRESS':
    case 'SERVICE_COMPLETE':
      return { bg: '#ede9fe', fg: '#5b21b6' };
    case 'READY':
    case 'READY_FOR_PICKUP':
    case 'VEHICLE_ACCEPTED':
      return { bg: '#d1fae5', fg: '#065f46' };
    case 'COMPLETED':
    case 'CAR_RETURNED':
    case 'SETTLED':
    case 'CLOSED':
    case 'ADMIN_CLOSED':
      return { bg: '#d1fae5', fg: '#065f46' };
    case 'CANCELLED':
    case 'REFUNDED':
    case 'VEHICLE_REJECTED':
    case 'DISPUTED':
      return { bg: '#fee2e2', fg: '#991b1b' };
    default:
      return { bg: '#e5e7eb', fg: '#374151' };
  }
}

export default function OrdersPage() {
  const auth = useAuth();
  const [garage, setGarage] = useState<GarageDetailResponse | null>(null);
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [counts, setCounts] = useState<OrderCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PartnerOrderFilter>('ALL');
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const isStaff =
    auth.user?.roles.includes('GARAGE_STAFF') ||
    auth.user?.roles.includes('GARAGE_SUPPORT') ||
    false;

  // Load the garage once — the list/counts effects key off garage.id.
  useEffect(() => {
    if (!auth.token) return;
    api
      .getMyGarage(auth.token)
      .then(setGarage)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load garage');
        setLoading(false);
      });
  }, [auth.token]);

  // Debounce the free-text search (~300ms); committing it resets to page 0.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Fetch the list whenever the filter, search, assignment, or page changes.
  useEffect(() => {
    if (!garage || !auth.token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listGarageOrdersFiltered(garage.id, activeTab, assignedToMe, page, PAGE_SIZE, auth.token, search)
      .then((res) => {
        if (cancelled) return;
        setOrders(res.content);
        setTotalPages(res.totalPages);
        setTotalElements(res.totalElements);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load orders');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [garage, auth.token, activeTab, assignedToMe, page, search]);

  // Counts are per-filter totals — they depend on search + assignment, not the
  // active tab or the page. Re-fetch alongside the list when those change.
  useEffect(() => {
    if (!garage || !auth.token) return;
    let cancelled = false;
    getGarageOrderCounts(garage.id, assignedToMe, auth.token, search)
      .then((c) => {
        if (!cancelled) setCounts(c);
      })
      .catch(() => {
        if (!cancelled) setCounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, [garage, auth.token, assignedToMe, search]);

  const handleTabChange = (tab: PartnerOrderFilter) => {
    setActiveTab(tab);
    setPage(0);
  };

  const handleAssignedToggle = () => {
    setAssignedToMe((v) => !v);
    setPage(0);
  };

  if (loading && !garage) return <div style={{ padding: 24 }}>Loading…</div>;
  if (error && !garage) return <div style={{ padding: 24, color: '#991b1b' }}>{error}</div>;

  const activeLabel = TABS.find((t) => t.value === activeTab)?.label ?? 'matching';

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto', fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>
          Orders
        </h1>
        {garage && (
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            {garage.name} · {totalElements} order{totalElements !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 16, maxWidth: 460 }}>
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af',
            pointerEvents: 'none',
            fontSize: '1rem',
            lineHeight: 1,
          }}
        >
          {/* magnifier glyph */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by customer or car…"
          onFocus={(e) => {
            e.currentTarget.style.borderColor = '#4f46e5';
            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = '#e2e8f0';
            e.currentTarget.style.boxShadow = 'none';
          }}
          style={{
            width: '100%',
            padding: '10px 12px 10px 40px',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            color: '#374151',
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.15s, box-shadow 0.15s',
          }}
        />
      </div>

      {/* Tabs + "My active orders" chip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map((tab) => {
          const isActive = activeTab === tab.value;
          const count = counts?.[tab.value];
          const hasCount = typeof count === 'number';
          return (
            <button
              key={tab.value}
              onClick={() => handleTabChange(tab.value)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 16px',
                borderRadius: 8,
                border: isActive ? '2px solid #4f46e5' : '1px solid #e2e8f0',
                background: isActive ? '#4f46e5' : 'white',
                color: isActive ? 'white' : '#374151',
                fontWeight: isActive ? 700 : 500,
                fontSize: '0.875rem',
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
            >
              {tab.label}
              {hasCount && (
                <span
                  style={{
                    display: 'inline-block',
                    minWidth: 18,
                    padding: '0 6px',
                    borderRadius: 999,
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    lineHeight: '18px',
                    textAlign: 'center',
                    background: isActive
                      ? 'rgba(255,255,255,0.2)'
                      : count === 0
                        ? '#f3f4f6'
                        : '#eef2ff',
                    color: isActive ? '#fff' : count === 0 ? '#94a3b8' : '#4f46e5',
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}

        {/* Staff-only chip */}
        {isStaff && (
          <button
            onClick={handleAssignedToggle}
            style={{
              marginLeft: 'auto',
              padding: '6px 14px',
              borderRadius: 20,
              border: assignedToMe ? '2px solid #4f46e5' : '1px solid #e2e8f0',
              background: assignedToMe ? '#4f46e5' : '#f8fafc',
              color: assignedToMe ? '#fff' : '#64748b',
              fontWeight: assignedToMe ? 700 : 400,
              fontSize: '0.8125rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'all 0.15s',
            }}
          >
            My active orders
          </button>
        )}
      </div>

      {/* Error inline (after initial load) */}
      {error && (
        <div style={{ marginBottom: 12, padding: 12, background: '#fee2e2', borderRadius: 8, color: '#991b1b', fontSize: '0.875rem' }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
            Loading orders…
          </div>
        ) : orders.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#6b7280' }}>
            <p style={{ fontSize: '1rem', fontWeight: 500 }}>
              {search ? 'No orders match your search.' : `No ${activeLabel.toLowerCase()}.`}
            </p>
            <p style={{ fontSize: '0.875rem', marginTop: 8 }}>
              {search
                ? 'Try a different customer name, car, or plate.'
                : 'Orders matching this filter will appear here.'}
            </p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={th}>Order #</th>
                <th style={th}>Status</th>
                <th style={th}>Customer</th>
                <th style={th}>Car</th>
                <th style={th}>Service</th>
                <th style={th}>Scheduled</th>
                <th style={th}>Amount</th>
                <th style={th}>Method</th>
                <th style={th}>Created</th>
                <th style={{ ...th, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const sc = statusColor(o.status);
                const carModel =
                  o.vehicleMakeModel ??
                  (o.vehicleSummary
                    ? [o.vehicleSummary.make, o.vehicleSummary.model].filter(Boolean).join(' ')
                    : '') ??
                  '';
                const carPlate = o.vehiclePlate ?? o.vehicleSummary?.plate ?? '';
                const options = selectedOptions(o).map((l) => l.label).join(', ');
                return (
                  <tr key={o.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={td}>
                      <Link
                        to={`/orders/${o.id}`}
                        style={{ color: '#4f46e5', textDecoration: 'none', fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8125rem' }}
                      >
                        {o.orderNumber ?? o.id.slice(0, 8) + '…'}
                      </Link>
                    </td>
                    <td style={td}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                        background: sc.bg, color: sc.fg, fontSize: '0.75rem', fontWeight: 600,
                      }}>
                        {statusLabel(o.status)}
                      </span>
                      {/* Spec 053 AC-21b — settlement status line, muted under the status badge.
                          `text-align: start` keeps it flush with the badge under [dir="rtl"]. */}
                      {o.settlementStatusLine?.label && (
                        <div
                          className="orders-settlement-line"
                          data-testid="settlement-status-line"
                          style={{ marginTop: 4, fontSize: '0.75rem', color: '#6b7280', lineHeight: 1.35, textAlign: 'start' }}
                        >
                          {o.settlementStatusLine.label}
                        </div>
                      )}
                    </td>
                    <td style={td}>{o.customerName || '—'}</td>
                    <td style={{ ...td, fontSize: '0.8125rem' }}>
                      {carModel || carPlate ? (
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}>
                          <span style={{ color: '#374151', fontWeight: 500 }}>{carModel || '—'}</span>
                          {carPlate && <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>{carPlate}</span>}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    {/* #1193 — the customer's selection (variant values or picked options) under the service. */}
                    <td style={td}>
                      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}>
                        <span>{o.serviceName ?? '—'}</span>
                        {options && (
                          <span data-testid="order-options" title={options} style={{ ...ellipsis, color: '#6b7280', fontSize: '0.75rem' }}>
                            {options}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={td}>
                      {(() => {
                        const slot = o.pickupTimeSlotLabel ?? o.pickupSlotLabel;
                        if (!o.scheduledDate && !slot) return '—';
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}>
                            <span>{o.scheduledDate ?? '—'}</span>
                            {slot && <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>{slot}</span>}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>
                      {o.totalAmount.toFixed(2)} {o.currency}
                    </td>
                    {/* Spec 053 Phase 3 — the badge is per-leg now ("Pickup by Garagat" /
                        "Return by Garagat" for mixed orders), with the self-leg instruction
                        copy underneath so the garage knows what the customer will do. */}
                    <td style={td}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                        background: hasGaragatLeg(o) ? '#ede9fe' : '#f0fdf4',
                        color: hasGaragatLeg(o) ? '#5b21b6' : '#166534',
                        fontSize: '0.75rem', fontWeight: 600,
                      }}>
                        {methodLabel(o)}
                      </span>
                      {selfLegHints(o).length > 0 && (
                        <div
                          data-testid="self-leg-hints"
                          style={{ marginTop: 4, fontSize: '0.75rem', color: '#6b7280', lineHeight: 1.35, textAlign: 'start' }}
                        >
                          {selfLegHints(o).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td style={td}>{new Date(o.createdAt).toLocaleDateString()}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Link to={`/orders/${o.id}`} style={viewBtn}>View</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button disabled={page === 0 || loading} onClick={() => setPage((p) => p - 1)} style={pgBtn}>
            ‹ Prev
          </button>
          <span style={{ padding: '8px 16px', color: '#6b7280', fontSize: '0.875rem' }}>
            Page {page + 1} of {totalPages}
          </span>
          <button disabled={page >= totalPages - 1 || loading} onClick={() => setPage((p) => p + 1)} style={pgBtn}>
            Next ›
          </button>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 16px',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const viewBtn: React.CSSProperties = {
  display: 'inline-block',
  padding: '6px 16px',
  borderRadius: 6,
  background: '#4f46e5',
  color: '#fff',
  textDecoration: 'none',
  fontSize: '0.8125rem',
  fontWeight: 600,
};

/** One line, cut with an ellipsis; the full text stays on hover. */
const ellipsis: React.CSSProperties = {
  maxWidth: 220,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const td: React.CSSProperties = {
  padding: '12px 16px',
  color: '#374151',
  verticalAlign: 'middle',
};

const pgBtn: React.CSSProperties = {
  padding: '6px 12px',
  border: '1px solid #d1d5db',
  background: 'white',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: '0.875rem',
};
